import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { LoadingState } from '../components/common/LoadingState';

/**
 * SSO Callback Page
 * Keycloak redirects here after the user authenticates on its hosted login page.
 * Exchanges the ?code= for tokens, then continues into the app.
 */
export const SSOCallbackPage: React.FC = () => {
  const navigate = useNavigate();
  const { completeSSOLogin } = useAuthStore();
  const [error, setError] = useState<string | null>(null);
  const ranOnce = useRef(false);

  useEffect(() => {
    if (ranOnce.current) return;
    ranOnce.current = true;

    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const ssoError = params.get('error_description') || params.get('error');

    if (ssoError) {
      setError(ssoError);
      return;
    }

    if (!code) {
      setError('No authorization code returned from the identity provider.');
      return;
    }

    // Strip ?code= from the address bar BEFORE exchanging it.
    //
    // An authorization code is single-use, and so is the PKCE verifier that
    // handleSSOCallback() deletes on first read. If this component is ever
    // mounted a second time while the code is still in the URL - a remount, a
    // refresh, the user pressing Back - the retry fails instantly with
    // "Missing PKCE verifier" rather than failing usefully. Removing it from
    // the URL makes a second attempt impossible: a remount then lands on the
    // !code branch above and reports a clear message.
    //
    // `ranOnce` alone is not enough: it is per-instance, so a remount gets a
    // fresh ref. This is the cross-instance guard.
    window.history.replaceState({}, '', window.location.pathname);

    completeSSOLogin(code)
      .then(() => navigate('/dashboard', { replace: true }))
      .catch((err) => setError(err instanceof Error ? err.message : 'SSO sign-in failed'));
  }, [completeSSOLogin, navigate]);

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-muted/30 px-4 dark:bg-card">
        <div className="w-full max-w-md space-y-4 text-center">
          <h1 className="text-xl font-semibold tracking-tight text-foreground">Sign-in failed</h1>
          <p className="text-sm text-rose-600 dark:text-rose-400">{error}</p>
          <button
            onClick={() => navigate('/login', { replace: true })}
            className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90"
          >
            Back to login
          </button>
        </div>
      </div>
    );
  }

  return <LoadingState message="Completing sign-in..." />;
};
